<script>
import { mapGetters } from 'vuex'
import baseMixin from '@/mixins/baseMixin'
import BookingDetailHeader from '@/components/booking/bookingList/side-panel/booking-detail-header.vue'
import BookingDetailsTab from '@/components/booking/bookingList/side-panel/booking-details-tab.vue'
import BookingDaysTable from '@/components/booking/bookingList/side-panel/booking-days-table.vue'
import BookingStatusLabelType from '@/shared/constants/booking/BookingStatusLabelType'

export default {
  name: 'BookingSidePanel',
  components: {
    BookingDetailHeader,
    BookingDetailsTab,
    BookingDaysTable,
  },
  mixins: [baseMixin],
  props: {
    value: { type: Boolean, default: false },
    booking: { type: Object, default: null },
  },
  data() {
    return {
      tab: 0,
      BookingStatusLabelType,
    }
  },
  computed: {
    ...mapGetters({
      getSelectedClients: 'client/getSelectedClients',
      hasCreateBookingPermissionForAtleastOneClientLocationByClientIds:
        'auth/hasCreateBookingPermissionForAtleastOneClientLocationByClientIds',
    }),
    visible: {
      get() {
        return this.value
      },
      set(v) {
        this.$emit('input', v)
      },
    },
    drawerWidth() {
      return this.$vuetify &&
        this.$vuetify.breakpoint &&
        this.$vuetify.breakpoint.smAndDown
        ? '100%'
        : 720
    },

    _groupMode() {
      return !!(
        this.booking &&
        this.booking.groupId &&
        this.booking.groupId !== 0
      )
    },

    // normalized blocks (either blockDetails or single outer booking)
    _blocks() {
      if (!this.booking) return []
      if (this._groupMode) {
        const arr = Array.isArray(this.booking.blockDetails)
          ? this.booking.blockDetails
          : []
        return arr
          .filter((b) => b && b.startDateTimeLocal && b.endDateTimeLocal)
          .map((b) => ({
            start: new Date(b.startDateTimeLocal),
            end: new Date(b.endDateTimeLocal),
            candidate: b.candidate || null,
            status: b.bookingStatus || '',
            isToday:
              new Date(b.startDateTimeLocal).toDateString() ===
              new Date().toDateString(),
            canEdit:
              this.canCreateBookings &&
              b.bookingStatus === 'Open' &&
              new Date(b.startDateTimeLocal) > new Date(),
          }))
          .sort((a, b) => a.start - b.start)
      }
      // single
      return [
        {
          start: this.booking.startDateTimeLocal
            ? new Date(this.booking.startDateTimeLocal)
            : null,
          end: this.booking.endDateTimeLocal
            ? new Date(this.booking.endDateTimeLocal)
            : null,
          candidate: this.booking.candidate || null,
          status: this.booking.bookingStatus || '',
          isToday:
            new Date(this.startDateTimeLocal).toDateString() ===
            new Date().toDateString(),
          canEdit:
            this.canCreateBookings &&
            this.booking.bookingStatus === 'Open' &&
            new Date(this.booking.startDateTimeLocal) > new Date(),
        },
      ].filter((b) => b.start && b.end)
    },

    daysCount() {
      return this._groupMode ? this._blocks.length : this._blocks.length ? 1 : 0
    },
    canCreateBookings() {
      return this.hasCreateBookingPermissionForAtleastOneClientLocationByClientIds(
        this.getSelectedClients
      )
    },
    isBlockEdit() {
      if (!this.booking) return false

      const hasBlocks =
        this.booking.blockDetails &&
        Array.isArray(this.booking.blockDetails) &&
        this.booking.blockDetails.length > 0

      const allBlocksOpenAndFuture =
        hasBlocks &&
        this.booking.blockDetails.every(
          (block) =>
            block.bookingStatus === 'Open' &&
            new Date(block.startDateTimeLocal) > new Date()
        )

      const bookingCount = hasBlocks ? this.booking.blockDetails.length : 0

      return (
        this.canCreateBookings &&
        this.booking.bookingStatus === 'Open' &&
        allBlocksOpenAndFuture &&
        bookingCount === this.booking.openCount
      )
    },
    isDuplicate() {
      if (!this.booking) return false

      const validStatuses = [
        BookingStatusLabelType.Open,
        BookingStatusLabelType.Pending,
        BookingStatusLabelType.Confirmed,
      ]

      let isValidStatus = false

      const hasBlocks =
        Array.isArray(this.booking.blockDetails) &&
        this.booking.blockDetails.length > 0

      if(this.canCreateBookings){
        if(hasBlocks){
          isValidStatus = hasBlocks &&
            this.booking.blockDetails.every(
              (block) =>
                validStatuses.includes(block.bookingStatus)
            )
        }else{
          isValidStatus = validStatuses.includes(this.booking.bookingStatus)
        }
      }

      return (
        this.canCreateBookings && isValidStatus
      )
    },
  },

  methods: {
    // display fallback
    _display(v) {
      if (v === null || v === undefined) return '-'
      if (typeof v === 'string' && v.trim() === '') return '-'
      return v
    },
    editBookingBlock() {
      this.$router.push({
        name: 'bookings-edit',
        query: {
          mode: 'block',
          bookingId: this.booking.id,
        },
      })
    },
  },
}
</script>

<template>
  <v-navigation-drawer
    v-model="visible"
    app
    fixed
    right
    temporary
    overlay-color="rgba(0,0,0,.35)"
    :width="drawerWidth"
    class="booking-side-panel"
  >
    <div class="close-btn">
      <div class="action-btn">
        <BaseButton
          icon="mdi-content-copy"
          fab
          tertiary
          circle
          sm
          hover-color="grey lighten-4"
          :class="isDuplicate ? 'panel-copy' : 'panel-copy-disabled'"
        />
        <BaseButton
          v-if="isBlockEdit"
          icon="mdi-pencil-outline"
          fab
          tertiary
          circle
          sm
          hover-color="grey lighten-4"
          class="panel-edit"
          :data-button="`booking-edit-${booking.id}`"
          @click="editBookingBlock"
        />
      </div>
      <BaseButton
        icon="mdi-close"
        fab
        tertiary
        circle
        sm
        hover-color="grey lighten-4"
        class="panel-close"
        @click="$emit('input', false)"
      />
    </div>

    <v-divider class="panel-divider"></v-divider>

    <!-- HEADER -->
    <BookingDetailHeader
      :booking="booking"
      :blocks="_blocks"
      :group-mode="_groupMode"
      :display="_display"
    />

    <v-divider class="panel-divider"></v-divider>

    <!-- TABS -->
    <div class="px-6">
      <v-tabs
        v-model="tab"
        show-arrows
        slider-color="#00ACEF"
        color="#E0E8EE"
        class="panel-tabs"
      >
        <v-tab class="panel-tab">{{ $t('sidePanel.details') }}</v-tab>
        <v-tab class="panel-tab"
          >{{ $t('sidePanel.days') }} ({{ daysCount }})</v-tab
        >
      </v-tabs>
    </div>

    <v-tabs-items v-model="tab" class="px-6">
      <BookingDetailsTab :booking="booking" :display="_display" />

      <!-- DAYS TAB -->
      <BookingDaysTable
        :blocks="_blocks"
        :display="_display"
        :booking-id="booking ? booking.id : 0"
      />
    </v-tabs-items>
  </v-navigation-drawer>
</template>

<style lang="scss" scoped>
@import '@/styles/_colors.scss';
@import '@/styles/bookingList/side-panel.scss';
</style>
